import { Fragment, useEffect, useRef, type ReactNode, type Ref } from 'react';
import { useGetIdentity, useMenu, usePermissions } from '@refinedev/core';
import { Link, useLocation } from 'react-router';
import {
  BarChart3,
  Bot,
  CircleCheck,
  Clock,
  Database,
  FileText,
  Fingerprint,
  Globe,
  KeyRound,
  Layers,
  PenLine,
  Server,
  Plug,
  Puzzle,
  ScrollText,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { isAdminRoles } from '@/lib/require-admin';
import { useDismissableOverlay } from '@/lib/use-dismissable-overlay';
import { useResourceCount } from '@/lib/use-resource-count';
import { cn } from '@/lib/utils';
import { useExtensionNavSections } from '@/extensions/use-extension-nav';
import { useHubVersionLabel } from '@/lib/hub-version';
import { useSidebarState } from './sidebar-state';

// Exported as a test seam: `sidebar-sections.test` asserts every nav item
// buildVisibleSections emits resolves to an icon, so a resource added to
// resources.ts without a glyph here fails the suite instead of shipping a
// naked row (how `db-connectors` slipped through in M24).
export const ICONS: Record<string, LucideIcon> = {
  // M28 — Workspace section (every authenticated user).
  documents: FileText,
  integrations: Plug,
  account: UserRound,
  users: Users,
  'api-keys': KeyRound,
  // M27 — Relations renamed to Permissions (UI only).
  permissions: ShieldCheck,
  // M27 — identity providers + web research (ADM-07/15).
  'identity-providers': Fingerprint,
  'web-research': Globe,
  // M27 — model registry + per-purpose assignment (ADM-01/03).
  models: Bot,
  assignments: SlidersHorizontal,
  // M24 — DB connectors (DBC-01). `Plug` is already the integrations glyph, so
  // the datastore reads as its own thing.
  'db-connectors': Database,
  // HUB-107 — MCP servers (copilot tools).
  'mcp-servers': Server,
  tracks: Layers,
  prompts: PenLine,
  // APPR-07 / D-16 — approvals queue.
  approvals: CircleCheck,
  // S08 — owner scheduled actions.
  schedules: Clock,
  // HUB-115 — single audit events (who did what); Usage is the aggregate.
  audit: ScrollText,
  // M33 — adoption surface over the audit log (ATRU-06).
  usage: BarChart3,
  settings: Settings,
  // HUB-103 — every extension admin row (custom pages + declared settings).
  extension: Puzzle,
};

// Section grouping mirrors what each resource is about; order within a
// section follows resources.ts order. M27 (ADM-10/11) groups Users +
// Identity Providers + Web Research + Permissions under one "Identity and
// Permissions" heading and adds an "AI" section for the model surfaces.
// These are the ADMIN sections — shown only to admins (M28 ADM-11).
const SECTIONS: { label: string; names: string[] }[] = [
  { label: 'Identity and Permissions', names: ['users', 'identity-providers', 'web-research', 'permissions'] },
  { label: 'AI', names: ['models', 'assignments', 'mcp-servers'] },
  { label: 'Directory', names: ['api-keys', 'tracks'] },
  { label: 'Copilot', names: ['prompts', 'approvals'] },
  // M33 — Usage sits beside Settings: a deployment-wide read, admin-only.
  // HUB-115 — Audit log leads the section: the event list the Usage counts
  // summarise. Nav entry is admin-only; the route itself is auth-only.
  { label: 'System', names: ['audit', 'usage', 'settings'] },
];

// M28 (ADM-11; COP-117 D-13) — the Workspace section is HARDCODED (like the
// sidebar brand block), not driven by refine resources, so a non-admin with no
// resources still sees it. Both links land on the /me/* shell; Documents opens
// the SAME component admins reach at /admin/documents (one component, no fork).
const WORKSPACE_ITEMS: NavItem[] = [
  { key: 'ws-documents', name: 'documents', route: '/me/documents', label: 'Documents' },
  { key: 'ws-integrations', name: 'integrations', route: '/me/integrations', label: 'Integrations' },
  // S08 — the owner's own schedules and runs (every hub call is user-scoped).
  { key: 'ws-schedules', name: 'schedules', route: '/admin/schedules', label: 'Scheduled actions' },
  { key: 'ws-account', name: 'account', route: '/me/account', label: 'Account' },
];

/**
 * The admin `SECTIONS` applied to any list of named resources — the sidebar
 * nav (refine's `useMenu` items) and the dashboard (`resources.ts`) share
 * this one grouping, so the two never disagree about where a section lives.
 * Any resource not claimed by a section lands in a trailing "Other" section,
 * so a new entry in resources.ts can't silently vanish from either surface.
 * Empty sections are dropped; items keep `SECTIONS` order within a section.
 */
export function groupBySection<T extends { name: string }>(all: readonly T[]): { label: string; items: T[] }[] {
  const byName = new Map(all.map((item) => [item.name, item]));
  const claimed = new Set(SECTIONS.flatMap((s) => s.names));
  const unclaimed = all.filter((item) => !claimed.has(item.name)).map((item) => item.name);
  const sectionDefs = unclaimed.length > 0 ? [...SECTIONS, { label: 'Other', names: unclaimed }] : SECTIONS;
  return sectionDefs
    .map((section) => ({
      label: section.label,
      items: section.names.map((name) => byName.get(name)).filter((item): item is T => item !== undefined),
    }))
    .filter((section) => section.items.length > 0);
}

// The minimal shape `buildVisibleSections` reads off refine's `useMenu` items.
export interface MenuItemLike {
  key: string;
  name: string;
  route?: string;
  label?: ReactNode;
}

export interface NavItem {
  key: string;
  name: string;
  route: string;
  label: ReactNode;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

/**
 * Pure role-gate for the sidebar (M28 ADM-11). Every authenticated user sees
 * the Workspace section; the admin sections (resolved from refine's menu
 * items) are appended ONLY for `isAdminRoles(roles)`. A non-admin therefore
 * sees ONLY Workspace — the same fail-closed decision `RequireAdmin` makes for
 * the /admin/* routes. Kept pure (no hooks) so it is unit-testable without a
 * refine/DOM context.
 */
export function buildVisibleSections(
  roles: unknown,
  menuItems: MenuItemLike[],
  extensionSections: NavSection[] = [],
): NavSection[] {
  const workspace: NavSection = { label: 'Workspace', items: WORKSPACE_ITEMS };
  if (!isAdminRoles(roles)) return [workspace];

  const adminSections = groupBySection(menuItems).map((section) => ({
    label: section.label,
    items: section.items.map((item) => ({
      key: item.key,
      name: item.name,
      route: item.route ?? '/',
      label: item.label ?? item.name,
    })),
  }));

  // HUB-103 — one section per enabled extension (headed by its name, so a row
  // like "Items" is never ambiguous), after the resource sections; absent when
  // no enabled extension has a page or a settings manifest.
  return [workspace, ...adminSections, ...extensionSections];
}

export interface SidebarBrand {
  subtitle: 'Admin' | 'Workspace';
  to: string;
  ariaLabel: string;
}

/**
 * The brand block names the area the user is in and links to its start. An
 * admin lands on the `/admin` dashboard; a non-admin cannot open it (RequireAdmin
 * sends them to `/`), so their block reads "Workspace" and links to the track
 * list (owner decision 2026-10-11).
 */
export function sidebarBrand(roles: unknown): SidebarBrand {
  return isAdminRoles(roles)
    ? { subtitle: 'Admin', to: '/admin', ariaLabel: 'Admin dashboard' }
    : { subtitle: 'Workspace', to: '/', ariaLabel: 'Home' };
}

export type SidebarVariant = 'expanded' | 'rail' | 'drawer';

export interface SidebarIdentity {
  email?: string;
  name?: string | null;
}

export interface SidebarNavProps {
  sections: NavSection[];
  variant: SidebarVariant;
  /** Whole-collection counts: a badge when expanded, a dot on the rail. */
  counts: Record<string, number | undefined>;
  pathname: string;
  brand: SidebarBrand;
  identity?: SidebarIdentity;
  hubVersion?: string;
  /** Drawer only — the close (X) button. */
  onClose?: () => void;
  /** Drawer only — the panel that takes focus when the drawer opens. */
  panelRef?: Ref<HTMLElement>;
}

function isActiveRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(route + '/');
}

// Rail items have no visible text, so the label must be a string for
// aria-label/title; a non-string label falls back to the resource name.
function navLabelText(item: NavItem): string {
  return typeof item.label === 'string' ? item.label : item.name;
}

function identityInitial(identity: SidebarIdentity | undefined): string {
  return (identity?.name || identity?.email || '?').trim().slice(0, 1).toUpperCase();
}

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function BrandLink({ brand, pathname, variant }: { brand: SidebarBrand; pathname: string; variant: SidebarVariant }) {
  const current = pathname === brand.to;
  if (variant === 'rail') {
    return (
      <Link
        to={brand.to}
        aria-label={brand.ariaLabel}
        aria-current={current ? 'page' : undefined}
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground',
          FOCUS_RING,
        )}
      >
        T
      </Link>
    );
  }
  // The brand block is the way back to the start (`sidebarBrand`): the
  // `/admin` dashboard for an admin — it has no nav entry of its own, so
  // without this a section is a dead end — and the track list otherwise.
  return (
    <Link
      to={brand.to}
      aria-label={brand.ariaLabel}
      aria-current={current ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2.5 rounded-[var(--radius-ctl)] px-1.5 py-1.5 transition-colors',
        variant === 'drawer' ? 'ml-3 min-w-0 flex-1' : 'mx-3 mt-3',
        FOCUS_RING,
        current ? 'bg-sidebar-accent' : 'hover:bg-sidebar-accent/40',
      )}
    >
      <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
        T
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold leading-tight">Trackframe</span>
        <span className="block text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          {brand.subtitle}
        </span>
      </span>
    </Link>
  );
}

function RailItem({ item, active, count }: { item: NavItem; active: boolean; count: number | undefined }) {
  const Icon = ICONS[item.name];
  const label = navLabelText(item);
  return (
    <Link
      to={item.route}
      aria-label={label}
      title={label}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex size-9 items-center justify-center rounded-[var(--radius-ctl)] transition-colors',
        FOCUS_RING,
        active ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent/40',
      )}
    >
      {Icon && <Icon className="size-4.5 shrink-0 opacity-80" aria-hidden="true" />}
      {count !== undefined && (
        <span className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-primary" aria-hidden="true" />
      )}
    </Link>
  );
}

function ListItem({
  item,
  active,
  count,
  variant,
}: {
  item: NavItem;
  active: boolean;
  count: number | undefined;
  variant: 'expanded' | 'drawer';
}) {
  const Icon = ICONS[item.name];
  return (
    <Link
      to={item.route}
      className={cn(
        'flex items-center rounded-[var(--radius-ctl)] px-3 transition-colors',
        variant === 'drawer' ? 'h-11 gap-3 text-[15px]' : 'gap-2.5 py-2 text-sm',
        FOCUS_RING,
        active ? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent/40',
      )}
    >
      {Icon && <Icon className="size-4 shrink-0 opacity-80" aria-hidden="true" />}
      <span className="truncate">{item.label ?? item.name}</span>
      {count !== undefined && (
        <span
          className={cn(
            'ml-auto rounded-full px-1.5 text-[10px] font-semibold tabular-nums leading-4',
            active ? 'bg-card/60 text-sidebar-accent-foreground' : 'border bg-muted text-muted-foreground',
          )}
        >
          {count}
        </span>
      )}
    </Link>
  );
}

function IdentityFooter({ identity, hubVersion }: { identity?: SidebarIdentity; hubVersion?: string }) {
  return (
    <div className="flex items-center gap-2.5 border-t border-sidebar-border p-3">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
        {identityInitial(identity)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold leading-tight">
          {identity?.name || identity?.email || '—'}
        </span>
        {identity?.name && identity?.email && (
          <span className="block truncate text-[10.5px] text-muted-foreground">{identity.email}</span>
        )}
      </span>
      <span className="text-[10px] tabular-nums text-muted-foreground">{hubVersion}</span>
    </div>
  );
}

function RailNav({ sections, counts, pathname, brand, identity }: SidebarNavProps) {
  return (
    <aside className="flex h-full w-14 shrink-0 flex-col items-center gap-2.5 border-r border-sidebar-border bg-sidebar py-3 text-sidebar-foreground">
      <BrandLink brand={brand} pathname={pathname} variant="rail" />
      <nav className="flex w-full flex-1 flex-col items-center gap-1.5 overflow-y-auto pt-1.5">
        {sections.map((section, i) => (
          <Fragment key={section.label}>
            {i > 0 && <span className="my-1 h-px w-6 shrink-0 bg-sidebar-border" aria-hidden="true" />}
            <ul className="flex flex-col items-center gap-1.5">
              {section.items.map((item) => (
                <li key={item.key}>
                  <RailItem item={item} active={isActiveRoute(pathname, item.route)} count={counts[item.name]} />
                </li>
              ))}
            </ul>
          </Fragment>
        ))}
      </nav>
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
        {identityInitial(identity)}
      </span>
    </aside>
  );
}

function ListNav({ sections, variant, counts, pathname, brand, identity, hubVersion, onClose, panelRef }: SidebarNavProps) {
  const isDrawer = variant === 'drawer';
  const listVariant = isDrawer ? 'drawer' : 'expanded';
  return (
    <aside
      ref={panelRef}
      {...(isDrawer ? { role: 'dialog', 'aria-modal': true, 'aria-label': 'Menu', tabIndex: -1 } : {})}
      className={cn(
        'flex flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground',
        isDrawer
          ? 'fixed inset-y-0 left-0 z-50 w-[300px] max-w-[85vw] shadow-lg outline-none'
          : 'h-full w-60 shrink-0',
      )}
    >
      {isDrawer ? (
        <div className="mt-3 flex items-center gap-1 pr-2">
          <BrandLink brand={brand} pathname={pathname} variant="drawer" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className={cn(
              'flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-[var(--radius-ctl)] text-muted-foreground transition-colors hover:bg-sidebar-accent/40 hover:text-foreground',
              FOCUS_RING,
            )}
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <BrandLink brand={brand} pathname={pathname} variant="expanded" />
      )}
      <nav className="flex-1 overflow-y-auto p-3">
        {sections.map((section) => (
          <div key={section.label}>
            <div className="px-3 pb-1 pt-4 text-[10.5px] font-semibold uppercase tracking-widest text-muted-foreground first:pt-1">
              {section.label}
            </div>
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.key}>
                  <ListItem
                    item={item}
                    active={isActiveRoute(pathname, item.route)}
                    count={counts[item.name]}
                    variant={listVariant}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <IdentityFooter identity={identity} hubVersion={hubVersion} />
    </aside>
  );
}

/**
 * Presentational nav, rendered from data so every variant is testable with a
 * static render: `expanded` (desktop, w-60), `rail` (desktop collapsed, icons
 * only, w-14) and `drawer` (phone overlay panel with 44px rows).
 */
export function SidebarNav(props: SidebarNavProps) {
  const sections = props.sections.filter((section) => section.items.length > 0);
  return props.variant === 'rail' ? <RailNav {...props} sections={sections} /> : <ListNav {...props} sections={sections} />;
}

export function Sidebar({ variant }: { variant: SidebarVariant }) {
  const { menuItems } = useMenu();
  const location = useLocation();
  const { data: identity } = useGetIdentity<{ email?: string; name?: string | null }>();
  const { data: roles } = usePermissions<unknown>({});
  const { closeDrawer } = useSidebarState();

  // Fixed hook set — counts for the whole-collection resources shown as
  // right-aligned badges (others render without one).
  const counts: Record<string, number | undefined> = {
    users: useResourceCount('users'),
    'api-keys': useResourceCount('api-keys'),
    tracks: useResourceCount('tracks'),
  };

  const extensionSections = useExtensionNavSections(roles);
  const sections = buildVisibleSections(roles, menuItems as MenuItemLike[], extensionSections);

  const hubVersion = useHubVersionLabel();
  const brand = sidebarBrand(roles);

  // Drawer: Esc + body scroll lock; focus the panel on open and hand focus
  // back to whatever opened it (the top-bar menu button) on close.
  const isDrawer = variant === 'drawer';
  const panelRef = useRef<HTMLElement>(null);
  useDismissableOverlay(isDrawer, closeDrawer);
  useEffect(() => {
    if (!isDrawer) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    return () => opener?.focus();
  }, [isDrawer]);

  return (
    <SidebarNav
      sections={sections}
      variant={variant}
      counts={counts}
      pathname={location.pathname}
      brand={brand}
      identity={identity}
      hubVersion={hubVersion}
      onClose={isDrawer ? closeDrawer : undefined}
      panelRef={isDrawer ? panelRef : undefined}
    />
  );
}
